import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { CalendarClock, MapPin, Send, ArrowLeft, Loader2, CheckCircle, Shield, Phone } from 'lucide-react';

/**
 * RequestAppointmentPage — public form where a patient submits an
 * on-request appointment ask. Now includes:
 *   - 10-digit mobile validation
 *   - MSG91 OTP verification before submission
 *   - service_type radio (Consult / Minor Procedure / Surgery)
 *   - notes_for_doctor open textarea (replaces the free-form "reason")
 *
 * Route: /dr/:slug/request-appointment?location_id=<id>
 */
const SERVICE_TYPES = [
  { key: 'consult',   label: 'Consult',           hint: 'Outpatient visit / second opinion' },
  { key: 'procedure', label: 'Minor Procedure',   hint: 'Outpatient procedure under local anaesthesia' },
  { key: 'surgery',   label: 'Surgery',           hint: 'Day-care or admitted surgery' },
];

export default function RequestAppointmentPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const locationId = params.get('location_id') || '';

  const [doctor, setDoctor] = useState(null);
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    patient_name: '',
    patient_phone: '',
    service_type: 'consult',
    notes_for_doctor: '',
    preferred_days: [],
  });

  // OTP state
  const [otpStage, setOtpStage] = useState('idle'); // idle | sending | sent | verifying | verified
  const [otpToken, setOtpToken] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpDev, setOtpDev] = useState(''); // surfaced in SMS_DEV_MODE for QA convenience

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/doctor/${slug}`);
        setDoctor(data);
        const loc = (data.locations || []).find(l => l.id === locationId);
        setLocation(loc || null);
        if (!loc) setError('Location not found.');
        else if ((loc.availability_mode || 'scheduled') !== 'on_request_only') {
          setError('This location accepts regular bookings — please use the Slot Picker on the main page.');
        }
      } catch (e) { setError('Could not load doctor profile.'); }
      finally { setLoading(false); }
    })();
  }, [slug, locationId]);

  const phoneDigits = (form.patient_phone || '').replace(/\D/g, '');
  const phoneValid = phoneDigits.length === 10;

  const toggleDay = (d) => {
    setForm(f => ({
      ...f,
      preferred_days: f.preferred_days.includes(d)
        ? f.preferred_days.filter(x => x !== d)
        : [...f.preferred_days, d].slice(0, 5),
    }));
  };

  // Generate the next 14 days.
  const upcomingDays = Array.from({ length: 14 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() + i);
    return d.toISOString().slice(0, 10);
  });

  // OTP flow — uses the existing /api/auth/send-otp + /api/auth/verify-otp.
  const sendOtp = async () => {
    setError('');
    if (!phoneValid) { setError('Please enter a valid 10-digit mobile number'); return; }
    setOtpStage('sending');
    try {
      const { data } = await api.post('/auth/send-otp', { phone: phoneDigits });
      setOtpToken(data.otp_token || '');
      setOtpDev(data._dev_otp || '');
      setOtpStage('sent');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not send OTP');
      setOtpStage('idle');
    }
  };
  const verifyOtp = async () => {
    setError('');
    if (!otpCode.trim()) { setError('Enter the OTP you received'); return; }
    setOtpStage('verifying');
    try {
      await api.post('/auth/verify-otp', { otp_token: otpToken, code: otpCode.trim() });
      setOtpStage('verified');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Incorrect OTP');
      setOtpStage('sent');
    }
  };

  const submit = async () => {
    if (otpStage !== 'verified') {
      setError('Please verify your phone with OTP first.');
      return;
    }
    setSubmitting(true); setError('');
    try {
      const { data } = await api.post(`/public/dr/${slug}/appointment-requests`, {
        location_id: locationId,
        patient_name: form.patient_name,
        patient_phone: phoneDigits,
        service_type: form.service_type,
        notes_for_doctor: form.notes_for_doctor,
        preferred_days: form.preferred_days,
        otp_token: otpToken,
      });
      setSubmitted(data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not submit your request.');
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-slate-400 text-sm">
        <Loader2 size={20} className="animate-spin mr-2" /> Loading…
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="max-w-xl mx-auto px-4 py-12" data-testid="request-submitted">
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50/50 p-6 text-center space-y-3">
          <CheckCircle size={36} className="mx-auto text-emerald-600" />
          <h2 className="text-xl font-semibold text-slate-900">Request sent</h2>
          <p className="text-sm text-slate-600">
            Your doctor will see your request live and offer a 30-min window. Track status + confirm slot on the next page.
          </p>
          <Link to={`/appointment-request/${submitted.id}`}
            className="inline-block rounded-full px-5 py-2 bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700">
            Open my appointment pass →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50/50 via-white to-orange-50/30 px-4 py-8" data-testid="request-appointment-page">
      <div className="max-w-xl mx-auto">
        {/* Top bar: back link + DocSite mark */}
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => navigate(`/dr/${slug}`)} className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800">
            <ArrowLeft size={12} /> Back to profile
          </button>
          <a href="/" className="flex items-center gap-1.5" data-testid="req-docsite-logo">
            <img src="/docsite-logo.png" alt="DocSite" className="h-7 w-7 object-contain" />
            <span className="text-xs font-bold text-slate-700 tracking-tight">DocSite</span>
          </a>
        </div>

        {/* Branded ticket-style card — mirrors the AppointmentPass aesthetic */}
        <div className="rounded-2xl border-2 border-amber-200 bg-white shadow-xl overflow-hidden">
          {/* Header band */}
          <div className="bg-gradient-to-r from-amber-600 to-orange-500 text-white px-5 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img src="/docsite-logo.png" alt="DocSite" className="h-8 w-8 object-contain bg-white/10 rounded-md p-0.5" />
              <div>
                <p className="text-[10px] uppercase tracking-widest font-bold opacity-90">DocSite Booking Request</p>
                <p className="text-sm font-bold leading-tight">On Appointment Basis</p>
              </div>
            </div>
            <CalendarClock size={20} />
          </div>

          {/* Hospital + doctor strip */}
          <div className="px-5 py-4 bg-amber-50/60 border-b border-amber-100">
            <p className="text-[10px] uppercase tracking-widest font-bold text-amber-700">Requesting</p>
            <p className="text-base font-bold text-slate-900 mt-0.5">
              Dr. {doctor?.name?.split(' ').slice(-1)[0]} <span className="text-slate-400 font-normal">·</span> {location?.name}
            </p>
            {location?.role_line && (
              <p className="text-xs font-semibold text-violet-700 mt-1">{location.role_line}</p>
            )}
            <p className="text-[12px] text-slate-600 mt-2 leading-snug">
              Your doctor visits this hospital <b>only on patient request</b>. Submit your ask — they'll offer a 30-min exclusive window in real-time. You confirm; the slot freezes on both calendars.
            </p>
            {location && (
              <div className="flex items-center gap-1.5 text-[11px] text-slate-500 mt-2">
                <MapPin size={11} /> {location.address}{location.city ? `, ${location.city}` : ''}
              </div>
            )}
          </div>

          {error && (
            <div className="mx-5 mt-4 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700" data-testid="request-error">
              {error}
            </div>
          )}

          {/* Form body */}
          <div className="px-5 py-5 space-y-4">
        <div>
          <label className="text-xs font-semibold text-slate-700 mb-1 block">Patient Name *</label>
          <input type="text" value={form.patient_name}
            onChange={(e) => setForm(f => ({ ...f, patient_name: e.target.value }))}
            placeholder="Full name"
            className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
            data-testid="req-patient-name" />
        </div>

        {/* Patient Mobile + OTP */}
        <div>
          <label className="text-xs font-semibold text-slate-700 mb-1 block">Patient Mobile *</label>
          <div className="flex gap-2 items-stretch">
            <input
              type="tel"
              inputMode="numeric"
              maxLength={10}
              value={form.patient_phone}
              onChange={(e) => {
                const v = e.target.value.replace(/\D/g, '').slice(0, 10);
                setForm(f => ({ ...f, patient_phone: v }));
                // Reset OTP state if the patient edits the number after sending.
                if (otpStage !== 'idle') {
                  setOtpStage('idle'); setOtpToken(''); setOtpCode(''); setOtpDev('');
                }
              }}
              disabled={otpStage === 'verified'}
              placeholder="10-digit mobile"
              className="flex-1 rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 disabled:bg-slate-50"
              data-testid="req-patient-phone" />
            {otpStage === 'idle' && (
              <button type="button" onClick={sendOtp} disabled={!phoneValid}
                className="rounded-xl px-3 py-2.5 bg-sky-600 text-white text-xs font-semibold hover:bg-sky-700 disabled:opacity-50 whitespace-nowrap"
                data-testid="otp-send-btn">
                Send OTP
              </button>
            )}
            {otpStage === 'sending' && (
              <span className="inline-flex items-center px-3 text-xs text-slate-500"><Loader2 size={12} className="animate-spin mr-1" /> Sending…</span>
            )}
            {otpStage === 'verified' && (
              <span className="inline-flex items-center gap-1 px-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-semibold" data-testid="otp-verified-chip">
                <CheckCircle size={12} /> Verified
              </span>
            )}
          </div>
          {!phoneValid && form.patient_phone && (
            <p className="text-[11px] text-rose-600 mt-1">Phone must be exactly 10 digits.</p>
          )}
          {(otpStage === 'sent' || otpStage === 'verifying') && (
            <div className="mt-2 flex gap-2 items-stretch" data-testid="otp-verify-row">
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="Enter OTP"
                className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-amber-500/20"
                data-testid="otp-input" />
              <button type="button" onClick={verifyOtp} disabled={otpStage === 'verifying' || !otpCode}
                className="rounded-xl px-3 py-2 bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 disabled:opacity-50 whitespace-nowrap"
                data-testid="otp-verify-btn">
                {otpStage === 'verifying' ? 'Verifying…' : 'Verify'}
              </button>
              <button type="button" onClick={sendOtp}
                className="text-[11px] text-slate-500 hover:text-slate-800 underline"
                data-testid="otp-resend-btn">
                Resend
              </button>
            </div>
          )}
          {otpDev && otpStage === 'sent' && (
            <p className="text-[10px] text-slate-400 mt-1">
              <Shield size={9} className="inline" /> Dev OTP: <b className="font-mono">{otpDev}</b>
            </p>
          )}
        </div>

        {/* ── Locked section — service type, notes, day-picker only
              unlock when patient name is filled AND phone is OTP-verified. */}
        {(() => {
          const unlocked = !!form.patient_name.trim() && otpStage === 'verified';
          if (!unlocked) {
            return (
              <div className="rounded-xl border-2 border-dashed border-slate-200 bg-slate-50/60 px-4 py-5 text-center text-slate-500 text-xs" data-testid="locked-banner">
                <Shield size={16} className="inline mr-1 text-slate-400" />
                Please enter <b>Patient Name</b> and verify <b>Patient Mobile</b> via OTP to continue.
              </div>
            );
          }
          return (
            <>
              {/* Service type */}
              <div data-testid="gated-service-block">
                <label className="text-xs font-semibold text-slate-700 mb-1.5 block">What is this visit for? *</label>
                <div className="grid grid-cols-3 gap-2">
                  {SERVICE_TYPES.map(s => (
                    <button key={s.key} type="button"
                      onClick={() => setForm(f => ({ ...f, service_type: s.key }))}
                      className={`rounded-xl border px-3 py-2.5 text-left transition-colors ${form.service_type === s.key ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-slate-700 border-slate-200 hover:border-amber-300'}`}
                      data-testid={`req-service-${s.key}`}>
                      <div className="text-xs font-semibold">{s.label}</div>
                      <div className={`text-[10px] mt-0.5 ${form.service_type === s.key ? 'text-white/80' : 'text-slate-500'}`}>{s.hint}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Notes by patient for doctor */}
              <div>
                <label className="text-xs font-semibold text-slate-700 mb-1 block">Notes by Patient for Doctor (optional)</label>
                <textarea value={form.notes_for_doctor}
                  onChange={(e) => setForm(f => ({ ...f, notes_for_doctor: e.target.value }))}
                  placeholder="Describe symptoms, history, what you'd like to discuss…"
                  rows={3}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
                  data-testid="req-notes" />
              </div>

              {/* Day picker — only enabled if at least the notes textarea has been touched (i.e., patient has had a chance to fill optional) */}
              <div>
                <label className="text-xs font-semibold text-slate-700 mb-1.5 block">Pick 1–5 days that suit you (optional)</label>
                <div className="grid grid-cols-7 gap-1.5">
                  {upcomingDays.map(d => {
                    const dt = new Date(d);
                    const isPicked = form.preferred_days.includes(d);
                    return (
                      <button key={d} type="button" onClick={() => toggleDay(d)}
                        className={`px-2 py-2 rounded-lg text-[10px] font-semibold border transition-colors ${isPicked ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-slate-600 border-slate-200 hover:border-amber-300'}`}
                        data-testid={`req-day-${d}`}>
                        <div className="font-mono">{dt.toLocaleDateString(undefined, { weekday: 'short' })[0]}</div>
                        <div className="text-[11px]">{dt.getDate()}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <button onClick={submit}
                disabled={submitting || !form.patient_name.trim() || otpStage !== 'verified' || !!error}
                className="w-full inline-flex items-center justify-center gap-1.5 rounded-full px-5 py-3 bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600 disabled:opacity-50"
                data-testid="req-submit">
                <Send size={14} /> {submitting ? 'Sending…' : 'Send request to doctor'}
              </button>
              <p className="text-[11px] text-slate-400 leading-snug text-center">
                By submitting, you understand the doctor visits exclusively at your request. A no-show fee of ₹500 applies on your next visit if you confirm a slot and don't show up.
              </p>
            </>
          );
        })()}
          </div>

          {/* DocSite footer brand — matches the AppointmentPass */}
          <div className="px-5 py-2.5 bg-slate-900 text-white text-center flex items-center justify-center gap-1.5">
            <img src="/docsite-logo.png" alt="DocSite" className="h-4 w-4 object-contain" />
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold">Powered by DocSite Platform™</p>
          </div>
        </div>

        <p className="text-[11px] text-slate-400 mt-3 text-center">
          Your details are encrypted end-to-end. DocSite never shares your phone with third parties.
        </p>
      </div>
    </div>
  );
}
